"use client";

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  /** The confirm button's text while busy, in the page's language (default: English "Working…"). */
  busyLabel?: string;
  onConfirm: () => void | Promise<void>;
  /**
   * Switch the confirm off while the dialog stays open and readable, e.g. a
   * role that lost the grant the confirmed call checks (pass the permission
   * gate's `denied`). Cancel keeps working. Off, a click confirms nothing.
   */
  confirmDisabled?: boolean;
  /**
   * Why the confirm is off (e.g. the gate's "Your role can't do this"): a
   * visible line under the description, the confirm's accessible description
   * and its `title`. Shown only while `confirmDisabled`.
   */
  confirmDisabledReason?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  busyLabel = "Working…",
  onConfirm,
  confirmDisabled = false,
  confirmDisabledReason,
}: ConfirmDialogProps) {
  const reasonId = React.useId();
  const reason = confirmDisabled && confirmDisabledReason ? confirmDisabledReason : undefined;

  const handleConfirm = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (confirmDisabled) return;
    await onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : null}
          {reason ? (
            <p
              id={reasonId}
              className="text-sm font-medium text-muted-foreground"
              data-permission-denied="true"
            >
              {reason}
            </p>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          {/* The variant, not a className: AlertDialogAction is a Button through
              asChild, and a className only joins its classes, so bg-primary
              outranked bg-destructive and "Delete" was never red. */}
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={busy || confirmDisabled}
            aria-describedby={reason ? reasonId : undefined}
            title={reason}
            variant={destructive ? "destructive" : "default"}
          >
            {busy ? busyLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
